import { useState } from 'react';
import { supabaseAdmin } from '@/integrations/supabase/admin';
import { Product } from '@/lib/index';

interface ProductFormData {
  name: string;
  name_ar: string;
  description: string;
  description_ar: string;
  price: number;
  original_price?: number;
  category: string;
  brand: string;
  image: string;
  images?: string[];
  stock: number;
  rating?: number;
  review_count?: number;
  views?: number;
  featured: boolean;
}

export function useAdminProducts() {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  // إضافة منتج جديد
  const addProduct = async (productData: ProductFormData) => {
    try {
      setLoading(true);
      
      console.log('🔍 Attempting to add product:', productData);
      
      const { data, error } = await supabaseAdmin
        .from('products')
        .insert([productData])
        .select()
        .single();

      if (error) {
        console.error('❌ Supabase error:', error);
        console.error('Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        throw error;
      }

      console.log('✅ Product added successfully:', data);
      return { success: true, data };
    } catch (error) {
      console.error('❌ Error adding product:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to add product' 
      };
    } finally {
      setLoading(false);
    }
  };

  // تحديث منتج
  const updateProduct = async (id: string, productData: Partial<ProductFormData>) => {
    try {
      setLoading(true);
      const { data, error } = await supabaseAdmin
        .from('products')
        .update(productData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('Error updating product:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to update product' 
      };
    } finally {
      setLoading(false);
    }
  };

  // حذف منتج
  const deleteProduct = async (id: string) => {
    try {
      setLoading(true);
      const { error } = await supabaseAdmin
        .from('products')
        .delete()
        .eq('id', id);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error deleting product:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to delete product' 
      };
    } finally {
      setLoading(false);
    }
  };

  // رفع صورة إلى Storage
  const uploadImage = async (file: File, folder: string = 'products') => {
    try {
      setUploading(true);
      
      // إنشاء اسم فريد للملف
      const fileExt = file.name.split('.').pop();
      const fileName = `${folder}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

      // رفع الملف
      const { data, error } = await supabaseAdmin.storage
        .from('product-images')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // الحصول على URL العام
      const { data: { publicUrl } } = supabaseAdmin.storage
        .from('product-images')
        .getPublicUrl(fileName);

      return { success: true, url: publicUrl };
    } catch (error) {
      console.error('Error uploading image:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to upload image' 
      };
    } finally {
      setUploading(false);
    }
  };

  // حذف صورة من Storage
  const deleteImage = async (imageUrl: string) => {
    try {
      // استخراج اسم الملف من URL
      const urlParts = imageUrl.split('/');
      const fileName = urlParts.slice(-2).join('/'); // folder/filename

      const { error } = await supabaseAdmin.storage
        .from('product-images')
        .remove([fileName]);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error deleting image:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to delete image' 
      };
    }
  };

  return {
    loading,
    uploading,
    addProduct,
    updateProduct,
    deleteProduct,
    uploadImage,
    deleteImage,
  };
}

// Hook لإدارة الماركات
export function useAdminBrands() {
  const [loading, setLoading] = useState(false);

  const addBrand = async (name: string, name_ar: string) => {
    try {
      setLoading(true);
      const { data, error } = await supabaseAdmin
        .from('brands')
        .insert([{ name, name_ar }])
        .select()
        .single();

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('Error adding brand:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to add brand' 
      };
    } finally {
      setLoading(false);
    }
  };

  const deleteBrand = async (name: string) => {
    try {
      setLoading(true);
      const { error } = await supabaseAdmin
        .from('brands')
        .delete()
        .eq('name', name);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error deleting brand:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to delete brand' 
      };
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    addBrand,
    deleteBrand,
  };
}